import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

const KaiContext = createContext({
  contextProductId: null,
  setContextProduct: () => {},
  screenContext: null,
  setScreenContext: () => {},
  patchScreenContext: () => {},
  registerProductDraftBridge: () => {},
  registerTemplateCustomizerBridge: () => {},
  registerCatalogHelpers: () => {},
  openProductGuidance: () => {},
  openSettingsGuidance: () => {},
  guidanceRequest: null,
  settingsGuidanceRequest: null,
  clearGuidanceRequest: () => {},
  clearSettingsGuidanceRequest: () => {},
  productDraftBridge: null,
  settingsBridge: null,
  templateCustomizerBridge: null,
  catalogHelpers: null,
  registerProductSaveHandler: () => {},
  productSaveHandler: null,
});

export function KaiContextProvider({ children }) {
  const [contextProductId, setContextProductId] = useState(null);
  const [screenContext, setScreenContextState] = useState(null);
  const [guidanceRequest, setGuidanceRequest] = useState(null);
  const [settingsGuidanceRequest, setSettingsGuidanceRequest] = useState(null);
  const [draftRevision, setDraftRevision] = useState(0);
  const draftBridgeRef = useRef(null);
  const settingsBridgeRef = useRef(null);
  const templateBridgeRef = useRef(null);
  const catalogHelpersRef = useRef(null);
  const saveHandlerRef = useRef(null);

  const setContextProduct = useCallback((id) => setContextProductId(id || null), []);

  const setScreenContext = useCallback((next) => {
    if (typeof next === "function") {
      setScreenContextState((prev) => next(prev));
    } else {
      setScreenContextState(next);
    }
  }, []);

  const patchScreenContext = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setScreenContextState((prev) => ({ ...(prev || {}), ...patch }));
  }, []);

  const registerProductDraftBridge = useCallback((bridge) => {
    draftBridgeRef.current = bridge || null;
  }, []);

  const registerTemplateCustomizerBridge = useCallback((bridge) => {
    templateBridgeRef.current = bridge || null;
  }, []);

  const registerCatalogHelpers = useCallback((helpers) => {
    if (helpers === null) {
      catalogHelpersRef.current = null;
      return;
    }
    const next = { ...(catalogHelpersRef.current || {}) };
    for (const [k, v] of Object.entries(helpers)) {
      if (v === undefined) delete next[k];
      else next[k] = v;
    }
    catalogHelpersRef.current = Object.keys(next).length ? next : null;
  }, []);

  const registerSettingsBridge = useCallback((bridge) => {
    settingsBridgeRef.current = bridge || null;
  }, []);

  const openProductGuidance = useCallback((payload = {}) => {
    setGuidanceRequest({
      id: Date.now(),
      ...payload,
    });
  }, []);

  const openSettingsGuidance = useCallback((payload = {}) => {
    setSettingsGuidanceRequest({
      id: Date.now(),
      ...payload,
    });
  }, []);

  const clearSettingsGuidanceRequest = useCallback(() => {
    setSettingsGuidanceRequest(null);
  }, []);

  const clearGuidanceRequest = useCallback(() => {
    setGuidanceRequest(null);
  }, []);

  const notifyProductDraftChanged = useCallback(() => {
    setDraftRevision((r) => r + 1);
  }, []);

  const registerProductSaveHandler = useCallback((handler) => {
    saveHandlerRef.current = typeof handler === "function" ? handler : null;
  }, []);

  const value = useMemo(
    () => ({
      contextProductId,
      setContextProduct,
      screenContext,
      setScreenContext,
      patchScreenContext,
      registerProductDraftBridge,
      registerTemplateCustomizerBridge,
      registerSettingsBridge,
      get productDraftBridge() {
        return draftBridgeRef.current;
      },
      get settingsBridge() {
        return settingsBridgeRef.current;
      },
      get templateCustomizerBridge() {
        return templateBridgeRef.current;
      },
      get catalogHelpers() {
        return catalogHelpersRef.current;
      },
      get productSaveHandler() {
        return saveHandlerRef.current;
      },
      registerCatalogHelpers,
      registerProductSaveHandler,
      openProductGuidance,
      openSettingsGuidance,
      guidanceRequest,
      settingsGuidanceRequest,
      clearGuidanceRequest,
      clearSettingsGuidanceRequest,
      draftRevision,
      notifyProductDraftChanged,
    }),
    [
      contextProductId,
      setContextProduct,
      screenContext,
      setScreenContext,
      patchScreenContext,
      registerProductDraftBridge,
      registerTemplateCustomizerBridge,
      registerSettingsBridge,
      registerCatalogHelpers,
      openProductGuidance,
      openSettingsGuidance,
      guidanceRequest,
      settingsGuidanceRequest,
      clearGuidanceRequest,
      clearSettingsGuidanceRequest,
      draftRevision,
      notifyProductDraftChanged,
      registerProductSaveHandler,
    ]
  );

  return <KaiContext.Provider value={value}>{children}</KaiContext.Provider>;
}

export function useKaiContext() {
  return useContext(KaiContext);
}
